import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getHeaderNamesFromTable } from "../../api/headerName";
import {
  getHeaderNamesQueryKey,
  useSaveHeaderNames,
} from "../../hooks/useHeaderName";
import type { PartialFieldLabel } from "../../pages/search/context/FieldLabelContext";
import { DEFAULT_LABEL_MAPPING } from "../pages/search/columnDefs";

export const V2_TABLE_ID = "interactions";

/**
 * Fetches custom header names from the API and returns a `getLabel` helper
 * that resolves field -> display label, falling back to DEFAULT_LABEL_MAPPING.
 */
export function useV2HeaderNames(customerId: string | undefined) {
  const tableId = V2_TABLE_ID;
  const { t } = useTranslation("grid");

  const { data: headerNamesMap, isLoading } = useQuery({
    queryKey: getHeaderNamesQueryKey(customerId ?? "", tableId),
    queryFn: () => getHeaderNamesFromTable(customerId!, tableId),
    enabled: !!customerId,
  });

  const getLabel = useCallback(
    (field: string): string => {
      const custom = headerNamesMap?.get(field);
      if (custom?.name) return custom.name;
      // Prefer translated grid key, fall back to static mapping, then raw field
      const gridKey = field as Parameters<typeof t>[0];
      const translated = t(gridKey, { defaultValue: "" });
      if (translated) return translated;
      return DEFAULT_LABEL_MAPPING[field] ?? field;
    },
    [headerNamesMap, t],
  );

  return { getLabel, headerNamesMap, isLoading };
}

/**
 * Save hook for header names (for the ConfigureHeaderNames page).
 */
export function useV2SaveHeaderNames(customerId: string) {
  const { mutateAsync, isPending } = useSaveHeaderNames(customerId);

  const save = useCallback(
    async (fieldLabels: Record<string, PartialFieldLabel>) => {
      await mutateAsync({ tableId: V2_TABLE_ID, fieldLabels });
    },
    [mutateAsync],
  );

  return { save, isPending };
}
